// A browser-only copy of scene draft text that has not reached the server yet.
// Lets a reload or tab close during "Saving…" keep what the writer typed.

export const SCENE_DRAFT_PREFIX = 'brainstormy.sceneDraftUnsaved.v1:';
export const SCENE_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const keyFor = (storyId, sceneId, passId) => `${SCENE_DRAFT_PREFIX}${storyId}:${sceneId}:${passId}`;

function parse(raw) {
  try {
    const value = JSON.parse(raw);
    if (!value || typeof value !== 'object') return null;
    if (typeof value.text !== 'string' || typeof value.base !== 'string') return null;
    if (!Number.isFinite(value.at)) return null;
    return value;
  } catch {
    return null;
  }
}

export function writeUnsavedDraft(storyId, sceneId, passId, base, text) {
  if (!storyId || !sceneId || !passId) return;
  try {
    window.localStorage.setItem(
      keyFor(storyId, sceneId, passId),
      JSON.stringify({ base: base ?? '', text: text ?? '', at: Date.now() }),
    );
  } catch {
    // Storage may be full or unavailable.
  }
}

export function removeUnsavedDraft(storyId, sceneId, passId) {
  if (!storyId || !sceneId || !passId) return;
  try {
    window.localStorage.removeItem(keyFor(storyId, sceneId, passId));
  } catch {
    // Storage may be unavailable.
  }
}

// Remove the copy only when it holds exactly the text that was just saved.
export function removeUnsavedDraftIfSaved(storyId, sceneId, passId, savedText) {
  if (!storyId || !sceneId || !passId) return;
  try {
    const key = keyFor(storyId, sceneId, passId);
    const raw = window.localStorage.getItem(key);
    if (raw == null) return;
    const value = parse(raw);
    if (!value || value.text === savedText) window.localStorage.removeItem(key);
  } catch {
    // Storage may be unavailable.
  }
}

export function readUnsavedDraft(storyId, sceneId, passId, serverText) {
  if (!storyId || !sceneId || !passId) return null;
  try {
    const key = keyFor(storyId, sceneId, passId);
    const raw = window.localStorage.getItem(key);
    if (raw == null) return null;
    const value = parse(raw);
    const server = serverText ?? '';
    const fresh = value && Date.now() - value.at < SCENE_DRAFT_MAX_AGE_MS;
    if (fresh && value.base === server && value.text !== server) return value.text;
    window.localStorage.removeItem(key);
  } catch {
    // Storage may be unavailable.
  }
  return null;
}

export function clearAllSceneDrafts() {
  try {
    const store = window.localStorage;
    const keys = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (key && key.startsWith(SCENE_DRAFT_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => store.removeItem(key));
  } catch {
    // Storage may be unavailable.
  }
}

export function sweepOldSceneDrafts(maxAgeMs = SCENE_DRAFT_MAX_AGE_MS) {
  try {
    const store = window.localStorage;
    const now = Date.now();
    const stale = [];
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i);
      if (!key || !key.startsWith(SCENE_DRAFT_PREFIX)) continue;
      const value = parse(store.getItem(key));
      if (!value || now - value.at > maxAgeMs) stale.push(key);
    }
    stale.forEach((key) => store.removeItem(key));
  } catch {
    // Storage may be unavailable.
  }
}
